import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { AlertTriangle, CheckCircle2, XCircle, AlertCircle, Info } from "lucide-react";

/**
 * Reusable Rule Prompt Dialog Component
 * 
 * Displays rule prompts from shift/assignment creation and handles user Accept/Override actions.
 * Rules NEVER execute silently - this dialog ensures explicit user acknowledgment.
 * 
 * @param {boolean} open - Dialog open state
 * @param {function} onOpenChange - Dialog open change handler
 * @param {string} entityType - "shift" or "assignment"
 * @param {object} entity - The created entity (shift/assignment)
 * @param {array} rulePrompts - Array of RuleEvaluationResult objects
 * @param {array} availabilityConflicts - Optional availability conflicts (assignments only)
 * @param {function} onAcceptAll - Callback when user accepts all prompts
 * @param {function} onOverride - Callback when user overrides with reason
 * @param {function} onCancel - Callback when user cancels/closes
 * @param {boolean} loading - Loading state during submission
 */
export function RulePromptDialog({
  open,
  onOpenChange,
  entityType = "shift",
  entity,
  rulePrompts = [],
  availabilityConflicts = [],
  onAcceptAll,
  onOverride,
  onCancel,
  loading = false,
}) {
  const [overrideMode, setOverrideMode] = useState(false);
  const [overrideReasons, setOverrideReasons] = useState({});
  const [globalOverrideReason, setGlobalOverrideReason] = useState("");

  const hasRulePrompts = rulePrompts.length > 0;
  const hasConflicts = availabilityConflicts.length > 0;
  const hasAnyPrompts = hasRulePrompts || hasConflicts;

  // Get icon based on rule type - HIGH CONTRAST
  const getRuleIcon = (ruleType) => {
    switch (ruleType) {
      case "validation":
        return <AlertTriangle className="h-5 w-5 text-white" />;
      case "warning":
        return <AlertCircle className="h-5 w-5 text-white" />;
      case "auto_calculate":
        return <Info className="h-5 w-5 text-white" />;
      default:
        return <AlertCircle className="h-5 w-5 text-white" />;
    }
  };

  // Get background color based on rule type - HIGH CONTRAST (white text on coloured background)
  const getRuleBackground = (ruleType) => {
    switch (ruleType) {
      case "validation":
        return "bg-amber-600 border-amber-700";
      case "warning":
        return "bg-yellow-600 border-yellow-700";
      case "auto_calculate":
        return "bg-blue-600 border-blue-700";
      default:
        return "bg-surface-elevated border-border";
    }
  };

  const handleAcceptAll = () => {
    if (onAcceptAll) {
      const acceptances = rulePrompts.map((prompt) => ({
        rule_id: prompt.rule_id,
        action: "accept",
        override_reason: null,
      }));
      onAcceptAll(acceptances);
    }
  };

  const handleOverride = () => {
    if (!globalOverrideReason.trim()) {
      return;
    }
    
    if (onOverride) {
      const acceptances = rulePrompts.map((prompt) => ({
        rule_id: prompt.rule_id,
        action: "override",
        override_reason: overrideReasons[prompt.rule_id] || globalOverrideReason,
      }));
      onOverride(acceptances, globalOverrideReason);
    }
  };

  const handleCancel = () => {
    setOverrideMode(false);
    setOverrideReasons({});
    setGlobalOverrideReason("");
    if (onCancel) {
      onCancel();
    }
  };

  const handleClose = (openState) => {
    if (!openState) {
      handleCancel();
    }
    onOpenChange(openState);
  };

  if (!hasAnyPrompts) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-surface-base border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl text-foreground">
            <AlertTriangle className="h-6 w-6 text-amber-500" />
            {entityType === "shift" ? "Shift" : "Assignment"} Requires Attention
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            The following conditions require your review before proceeding.
            Rules never execute silently - please confirm or override each item.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Rule Prompts Section */}
          {hasRulePrompts && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">
                Rule Evaluations ({rulePrompts.length})
              </h3>
              
              {rulePrompts.map((prompt, index) => (
                <div
                  key={prompt.rule_id || index}
                  className={`rounded-lg border p-4 ${getRuleBackground(prompt.rule_type)}`}
                  data-testid={`rule-prompt-${prompt.rule_id}`}
                >
                  <div className="flex items-start gap-3">
                    {getRuleIcon(prompt.rule_type)}
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold text-white">
                          {prompt.rule_name}
                        </h4>
                        <span className="text-xs px-2 py-1 rounded-full bg-black/20 text-white uppercase font-medium">
                          {prompt.rule_type}
                        </span>
                      </div>
                      <p className="text-sm text-white/90 leading-relaxed">
                        {prompt.prompt_text}
                      </p>
                      
                      {/* Suggested Action - only show if it has meaningful, non-empty content */}
                      {prompt.suggested_action && 
                       typeof prompt.suggested_action === 'object' && 
                       Object.keys(prompt.suggested_action).length > 0 &&
                       Object.values(prompt.suggested_action).some(v => {
                         if (v === null || v === undefined || v === '') return false;
                         if (typeof v === 'object') return Object.keys(v).length > 0;
                         return true;
                       }) && (
                        <div className="mt-2 text-xs text-white/80 bg-black/20 rounded p-2">
                          <span className="font-medium">Suggested Action:</span>{" "}
                          {Object.entries(prompt.suggested_action)
                            .filter(([_, v]) => {
                              if (v === null || v === undefined || v === '') return false;
                              if (typeof v === 'object' && Object.keys(v).length === 0) return false;
                              return true;
                            })
                            .map(([k, v]) => {
                              // Format the key nicely
                              const formattedKey = k.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                              // Handle nested objects
                              if (typeof v === 'object' && v !== null) {
                                const innerEntries = Object.entries(v)
                                  .filter(([_, iv]) => iv !== null && iv !== undefined && iv !== '')
                                  .map(([ik, iv]) => `${ik.replace(/_/g, ' ')}: ${iv}`);
                                return innerEntries.length > 0 ? `${formattedKey} (${innerEntries.join(', ')})` : null;
                              }
                              return `${formattedKey}: ${v}`;
                            })
                            .filter(Boolean)
                            .join(', ')}
                        </div>
                      )}

                      {/* Individual override reason (optional) */}
                      {overrideMode && (
                        <div className="mt-3">
                          <Label className="text-xs text-muted-foreground">
                            Specific reason for this rule (optional)
                          </Label>
                          <Textarea
                            placeholder="Enter specific override reason..."
                            className="mt-1 h-16 bg-surface-input border-border text-foreground placeholder:text-muted-foreground"
                            value={overrideReasons[prompt.rule_id] || ""}
                            onChange={(e) =>
                              setOverrideReasons((prev) => ({
                                ...prev,
                                [prompt.rule_id]: e.target.value,
                              }))
                            }
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Availability Conflicts Section (Assignments only) */}
          {hasConflicts && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">
                Availability Conflicts ({availabilityConflicts.length})
              </h3>
              
              {availabilityConflicts.map((conflict, index) => (
                <div
                  key={`conflict-${index}`}
                  className="rounded-lg border p-4 bg-orange-500/10 border-orange-500/30"
                  data-testid={`availability-conflict-${index}`}
                >
                  <div className="flex items-start gap-3">
                    <XCircle className="h-5 w-5 text-orange-500 mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold text-white">
                          {conflict.staff_name}
                        </h4>
                        <span className="text-xs px-2 py-1 rounded-full bg-orange-500/20 text-orange-300 uppercase">
                          {conflict.conflict_type}
                        </span>
                      </div>
                      <p className="text-sm text-foreground mt-1">
                        {conflict.message}
                      </p>
                      {conflict.availability_reason && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Reason: {conflict.availability_reason}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Override Mode - Global Reason */}
          {overrideMode && (
            <div className="mt-6 p-4 rounded-lg bg-surface-elevated border border-border">
              <Label className="text-sm font-medium text-foreground">
                Override Reason (Required)
              </Label>
              <p className="text-xs text-muted-foreground mt-1 mb-2">
                Provide a reason for overriding these rules. This will be logged for audit purposes.
              </p>
              <Textarea
                placeholder="Enter the reason for overriding these rules..."
                className="h-24 bg-surface-input border-border text-foreground placeholder:text-muted-foreground"
                value={globalOverrideReason}
                onChange={(e) => setGlobalOverrideReason(e.target.value)}
                data-testid="override-reason-input"
              />
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          {!overrideMode ? (
            <>
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={loading}
                className="border-border text-foreground hover:bg-surface-hover"
                data-testid="rule-prompt-cancel"
              >
                Cancel
              </Button>
              <Button
                variant="outline"
                onClick={() => setOverrideMode(true)}
                disabled={loading}
                className="border-amber-600 text-amber-500 hover:bg-amber-500/10"
                data-testid="rule-prompt-override-mode"
              >
                Override...
              </Button>
              <Button
                onClick={handleAcceptAll}
                disabled={loading}
                className="bg-teal-600 hover:bg-teal-700 text-white"
                data-testid="rule-prompt-accept-all"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                    Processing...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4" />
                    Accept All
                  </span>
                )}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  setOverrideMode(false);
                  setGlobalOverrideReason("");
                }}
                disabled={loading}
                className="border-border text-foreground hover:bg-surface-hover"
              >
                Back
              </Button>
              <Button
                onClick={handleOverride}
                disabled={loading || !globalOverrideReason.trim()}
                className="bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50"
                data-testid="rule-prompt-confirm-override"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                    Processing...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    Confirm Override
                  </span>
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default RulePromptDialog;
